/**
 * Weidner Via6 — Shared amenities row body.
 *
 * Internal helper used by `AmenitiesListCommunity` and
 * `AmenitiesListInterior` to render a single amenities row (photo
 * slider on one side, paginated bullet list on the other). It is NOT
 * registered as a slot and has no `contentSchema` — the filename
 * prefix `_` marks it as a private rendering helper.
 *
 * Why this exists (history):
 *   The amenities page previously rendered both Community and Interior
 *   rows inside a single `AmenitiesList` slot inherited from
 *   @vibesites/theme-rise (wrapped here with Weidner styling). The
 *   Home-style DividerBG banner was requested between the two rows,
 *   but a single slot can't have a section inserted in the middle of
 *   it from pages.json. Splitting into two slots was the cleanest fix.
 *
 *   Rise's `AmenitiesList` exports only the top-level component — its
 *   internal `AmenityColumn`, `PaginatedAmenitiesList`, and
 *   `PhotoSlider` helpers are private — so we re-implement the row
 *   shell here. The CSS is the rise base verbatim, layered with the
 *   same Weidner overrides the legacy wrapper used to apply (navy
 *   panel, single-column underlined list, cursive heading accent,
 *   themed pagination controls). Visual parity with the previous
 *   merged section is the goal: the split affects DOM structure, not
 *   look.
 *
 * Contract:
 *   - The caller (AmenitiesListCommunity / AmenitiesListInterior) owns
 *     the slot, contentSchema, and `useCollectionBinding` /
 *     `RichTextContent` paths. It hands us already-resolved arrays
 *     plus a heading node and an image-position preference.
 *   - This helper only renders the row; no schema, no editor surface.
 *   - The cursive accent comes from the heading HTML the caller
 *     provides (it injects a `<span class="vibe-cursive-accent">…`).
 *     We load the Satisfy webfont once at the page level (de-duped by
 *     the `data-vibe-font="satisfy"` link marker the rest of the
 *     theme uses).
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

export interface AmenityItem {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

export interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback(
    (dir: 1 | -1) => {
      setCurrent((prev) => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image || ''}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            type="button"
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Paginated amenities list
//
// The image panel has a fixed height. The list panel inherits that same row
// height. If the list of items would render taller than the visible area,
// we slice it into pages and show prev/next controls.
//
// Approach (mirrors rise): a hidden "measurement" copy of the full list is
// rendered with the same width/layout but with no height constraint. We
// compare its natural scrollHeight to the available container height to
// decide how many items fit per page.
// ============================================================================

interface PaginatedAmenitiesListProps {
  items: AmenityItem[];
}

function PaginatedAmenitiesList({ items }: PaginatedAmenitiesListProps) {
  const [itemsPerPage, setItemsPerPage] = useState<number>(items.length || 1);
  const [page, setPage] = useState(0);

  const measureRef = useRef<HTMLUListElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (items.length === 0) {
      setItemsPerPage(1);
      return;
    }

    const measure = () => {
      const list = measureRef.current;
      const inner = innerRef.current;
      if (!list || !inner) return;
      const naturalH = list.scrollHeight;
      const availH = inner.clientHeight;
      if (availH <= 0) return;
      if (naturalH <= availH + 2) {
        setItemsPerPage(items.length);
        return;
      }
      // Approximate items-per-page from the height ratio. Items have similar
      // (but not identical) heights, so leave a small safety margin so the
      // last item on a page doesn't get clipped.
      const ratio = Math.max(0.05, (availH - 8) / naturalH);
      const ipp = Math.max(2, Math.floor(items.length * ratio));
      setItemsPerPage(ipp);
    };

    measure();
    setPage(0);

    if (typeof ResizeObserver !== 'undefined' && innerRef.current) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(innerRef.current);
      return () => ro.disconnect();
    }
  }, [items.length]);

  const safeItemsPerPage = Math.max(1, itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(items.length / safeItemsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const currentItems = items.slice(
    safePage * safeItemsPerPage,
    (safePage + 1) * safeItemsPerPage,
  );

  return (
    <div className="amenities-list-area">
      <div ref={innerRef} className="amenities-list-area-inner">
        {/* Hidden measurement pass: full list rendered with no height cap so
            we can read its natural scrollHeight. */}
        <ul
          ref={measureRef}
          className="amenities-col-list amenities-col-list--measure"
          aria-hidden="true"
        >
          {items.map((item, i) => (
            <li key={`m-${i}`} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>

        {/* Visible (paginated) list */}
        <ul className="amenities-col-list">
          {currentItems.map((item, i) => (
            <li key={`p${safePage}-${i}`} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>
      </div>

      {totalPages > 1 && (
        <div className="amenities-pagination">
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous amenities page"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="amenities-pagination-info">
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            aria-label="Next amenities page"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Satisfy webfont — for the cursive accent on the heading. De-duped against
// the rest of the theme via the `data-vibe-font="satisfy"` marker that
// PropertyOverview / FloorplansList / AmenitiesGrid / HomeMap / InteriorIntro
// / Header / the legacy AmenitiesList wrapper all use.
// ============================================================================

function useSatisfyFont() {
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);
}

// ============================================================================
// Public row component
// ============================================================================

export interface AmenitiesListRowProps {
  /**
   * The editable heading element supplied by the caller. Typically a
   * `<RichTextContent path="…heading" as="h3" className="amenities-col-heading" />`
   * whose default HTML wraps the leading word in
   * `<span class="vibe-cursive-accent">…</span>` for the brand accent.
   */
  headingNode: ReactNode;
  /**
   * Plain-text heading used as alt-text fallback on photos when neither
   * `alt_text` nor `title` is set on the gallery item.
   */
  headingText: string;
  /** Resolved amenity items for this row. */
  items: AmenityItem[];
  /** Resolved gallery photos for the slider. Empty → no image panel. */
  photos: GalleryPhoto[];
  /** Where the image panel sits on desktop / tablet (mobile is always top). */
  imagePosition: 'left' | 'right';
}

export function AmenitiesListRow({
  headingNode,
  headingText,
  items,
  photos,
  imagePosition,
}: AmenitiesListRowProps) {
  useSatisfyFont();
  const hasImage = photos.length > 0;
  const hasContent = items.length > 0;

  const contentPanel = (
    <div className="amenities-row-content">
      {headingNode}
      <PaginatedAmenitiesList items={items} />
    </div>
  );

  const imagePanel = hasImage ? (
    <div className="amenities-row-image">
      <PhotoSlider photos={photos} heading={headingText} />
    </div>
  ) : null;

  return (
    <section
      data-weidner-amenities-row
      className="weidner-amenities-row-section"
    >
      <div className="weidner-amenities-row-inner">
        {hasContent ? (
          <div
            className={`amenities-row amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
          >
            {imagePosition === 'left' ? (
              <>
                {imagePanel}
                {contentPanel}
              </>
            ) : (
              <>
                {contentPanel}
                {imagePanel}
              </>
            )}
          </div>
        ) : (
          <div className="weidner-amenities-row-empty">
            <p>Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Outer section — paints the navy backdrop behind the row, matching
           the legacy merged AmenitiesList override. */
        .weidner-amenities-row-section {
          padding: 2rem 1rem;
          background-color: var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .weidner-amenities-row-section {
            padding: 3rem 1.5rem;
          }
        }
        @media (min-width: 1024px) {
          .weidner-amenities-row-section {
            padding: 3rem 2rem;
          }
        }
        .weidner-amenities-row-inner {
          max-width: 72rem;
          margin: 0 auto;
        }
        .weidner-amenities-row-empty {
          padding: 3rem 0;
          text-align: center;
          color: var(--vibe-color-text-on-primary, #fff);
          font-family: var(--vibe-font-body);
        }

        /* Row layout: side-by-side image + content with a fixed height so the
           image gallery never grows tall. Mirrors rise's .amenities-row. */
        [data-weidner-amenities-row] .amenities-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          background: var(--vibe-color-primary);
          overflow: hidden;
          height: 460px;
        }
        [data-weidner-amenities-row] .amenities-row--no-image {
          grid-template-columns: 1fr;
          height: auto;
          min-height: 220px;
        }

        /* Content panel — navy bg (same as row), light text for contrast. */
        [data-weidner-amenities-row] .amenities-row-content {
          display: flex;
          flex-direction: column;
          padding: 0;
          min-height: 0;
          background-color: var(--vibe-color-primary);
        }

        /* Image panel fills its cell */
        [data-weidner-amenities-row] .amenities-row-image {
          position: relative;
          overflow: hidden;
          height: 100%;
        }
        [data-weidner-amenities-row] .amenities-row-image .amenities-slider {
          height: 100%;
        }

        @media (max-width: 900px) {
          [data-weidner-amenities-row] .amenities-row {
            height: 420px;
          }
        }
        @media (max-width: 768px) {
          [data-weidner-amenities-row] .amenities-row {
            grid-template-columns: 1fr;
            grid-template-rows: 220px 360px;
            height: auto;
          }
          [data-weidner-amenities-row] .amenities-row--no-image {
            grid-template-rows: auto;
            min-height: 220px;
          }
          /* Image on top regardless of desktop stagger. */
          [data-weidner-amenities-row] .amenities-row--img-right > .amenities-row-content {
            order: 2;
          }
          [data-weidner-amenities-row] .amenities-row--img-right > .amenities-row-image {
            order: 1;
          }
          [data-weidner-amenities-row] .amenities-row--img-left > .amenities-row-content {
            order: 2;
          }
          [data-weidner-amenities-row] .amenities-row--img-left > .amenities-row-image {
            order: 1;
          }
          [data-weidner-amenities-row] .amenities-row-image {
            height: 220px;
          }
          [data-weidner-amenities-row] .amenities-row-image .amenities-slider {
            height: 220px;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-row] .amenities-row {
            grid-template-rows: 200px 340px;
          }
          [data-weidner-amenities-row] .amenities-row-image,
          [data-weidner-amenities-row] .amenities-row-image .amenities-slider {
            height: 200px;
          }
        }

        /* Photo slider */
        [data-weidner-amenities-row] .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          flex-shrink: 0;
        }
        [data-weidner-amenities-row] .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        [data-weidner-amenities-row] .amenities-slider-img--active {
          opacity: 1;
        }

        [data-weidner-amenities-row] .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        [data-weidner-amenities-row] .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        [data-weidner-amenities-row] .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        [data-weidner-amenities-row] .amenities-slider-arrow--prev { left: 10px; }
        [data-weidner-amenities-row] .amenities-slider-arrow--next { right: 10px; }

        [data-weidner-amenities-row] .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        [data-weidner-amenities-row] .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        [data-weidner-amenities-row] .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        [data-weidner-amenities-row] .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          [data-weidner-amenities-row] .amenities-slider-img {
            transition: none;
          }
        }

        /* Heading — bigger than the rise default so it carries the navy
           panel, with the cursive accent treatment from the rest of the
           theme. */
        [data-weidner-amenities-row] .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--vibe-color-text-on-primary, #fff);
          text-align: left;
          padding: 1.5rem 2rem 0.25rem;
          margin: 0;
          letter-spacing: 0.02em;
        }
        @media (max-width: 900px) {
          [data-weidner-amenities-row] .amenities-col-heading {
            font-size: 1.875rem;
            padding: 1rem 1rem 0.25rem;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-row] .amenities-col-heading {
            font-size: 1.4rem;
            padding: 0.75rem 0.75rem 0.25rem;
            letter-spacing: 0.01em;
            text-align: center;
          }
        }
        [data-weidner-amenities-row] .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }

        /* List area takes the remaining vertical space inside the row */
        [data-weidner-amenities-row] .amenities-list-area {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        [data-weidner-amenities-row] .amenities-list-area-inner {
          flex: 1;
          position: relative;
          overflow: hidden;
          min-height: 0;
        }

        /* Single-column underlined list — bullets hidden, each item gets
           a bottom border spanning the column. Same treatment the legacy
           AmenitiesList wrapper applied. */
        [data-weidner-amenities-row] .amenities-col-list {
          list-style: none;
          padding: 0.75rem 2rem 0.75rem;
          margin: 0;
          display: grid;
          grid-template-columns: 1fr;
          gap: 0;
          align-content: start;
        }
        [data-weidner-amenities-row] .amenities-col-list--measure {
          position: absolute;
          inset: 0;
          visibility: hidden;
          pointer-events: none;
          height: auto;
        }
        @media (max-width: 900px) {
          [data-weidner-amenities-row] .amenities-col-list {
            padding: 0.5rem 1rem 0.5rem;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-row] .amenities-col-list {
            padding: 0.4rem 0.75rem 0.4rem;
          }
        }

        [data-weidner-amenities-row] .amenities-col-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.55rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text-on-primary, #fff);
          line-height: 1.5;
          border-bottom: 1px solid var(--vibe-color-text-on-primary, #fff);
        }
        @media (max-width: 900px) {
          [data-weidner-amenities-row] .amenities-col-item {
            font-size: 0.8rem;
            padding: 0.45rem 0;
            gap: 0.4rem;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-row] .amenities-col-item {
            font-size: 0.72rem;
            padding: 0.4rem 0;
            gap: 0.3rem;
            line-height: 1.35;
          }
        }
        [data-weidner-amenities-row] .amenities-col-bullet {
          display: none;
        }

        /* Pagination controls — only rendered when list overflows. */
        [data-weidner-amenities-row] .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          padding: 0.25rem 2rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        @media (max-width: 900px) {
          [data-weidner-amenities-row] .amenities-pagination {
            padding: 0.25rem 1rem 0.75rem;
            gap: 0.4rem;
            font-size: 0.78rem;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-row] .amenities-pagination {
            padding: 0.25rem 0.75rem 0.6rem;
            gap: 0.35rem;
            font-size: 0.72rem;
          }
          [data-weidner-amenities-row] .amenities-pagination-btn {
            width: 24px;
            height: 24px;
          }
          [data-weidner-amenities-row] .amenities-pagination-info {
            min-width: 2.5rem;
          }
        }
        [data-weidner-amenities-row] .amenities-pagination-btn {
          width: 28px;
          height: 28px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.35);
          background: transparent;
          color: var(--vibe-color-text-on-primary, #fff);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
        }
        [data-weidner-amenities-row] .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-text-on-primary, #fff);
          border-color: var(--vibe-color-text-on-primary, #fff);
          color: var(--vibe-color-primary);
        }
        [data-weidner-amenities-row] .amenities-pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        [data-weidner-amenities-row] .amenities-pagination-info {
          min-width: 3rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </section>
  );
}

export default AmenitiesListRow;
